import { memo, useMemo } from "react";
import Html from "react-pdf-html";

import type { FormSettings } from "components/pdf-generation-v3/form-print.utils";
import {
  DEFAULT_PDF_CSS_STYLE,
  FORM_PDF_CSS_STYLE,
  normalizePrintableHtml
} from "components/pdf-generation-v3/pdf-generation-v3.utils";

type PdfDocumentHtmlProps = {
  bodyHtml?: string | null;
  printableTableLayoutFixesEnabled?: boolean;
  formSettings?: FormSettings | null;
};

// Pre-compute static wrapper parts for better performance
const STATIC_WRAPPER_START = `<html><body><style>${DEFAULT_PDF_CSS_STYLE}</style>`;
const FORM_WRAPPER_START = `<html><body><style>${FORM_PDF_CSS_STYLE}</style>`;
const STATIC_WRAPPER_END = `</body></html>`;

function PdfDocumentHtmlSimple({
  bodyHtml,
  printableTableLayoutFixesEnabled,
  formSettings
}: PdfDocumentHtmlProps) {
  const content = useMemo(() => {
    if (!bodyHtml) return "";

    const normalized = normalizePrintableHtml(bodyHtml, {
      enableTableLayoutFixes: printableTableLayoutFixesEnabled,
      materializeRowspans: printableTableLayoutFixesEnabled,
      stripRowspanCellHeights: printableTableLayoutFixesEnabled,
      // PDF only — email HTML reaches a mail client, where `&nbsp;` already holds a line.
      preserveBlankLines: true,
      formSettings
    });
    const wrapperStart = formSettings
      ? FORM_WRAPPER_START
      : STATIC_WRAPPER_START;
    return `${wrapperStart}${normalized}${STATIC_WRAPPER_END}`;
  }, [bodyHtml, printableTableLayoutFixesEnabled, formSettings]);

  return <Html>{content}</Html>;
}

// Memoize component to prevent unnecessary re-renders
export default memo(PdfDocumentHtmlSimple);
